/**
 * Off-hand icons: a heater shield and a mage's focus (an orb held in a claw
 * cradle), tinted per tier material like the other equipment.
 */
import { PAL } from '../../palette';
import { Grid, alpha, mix, ramp, sphereLight, type Col } from '../env/raster';
import { finish, S } from './equip';
import type { Material } from './materials';

const IRON: readonly Col[] = [PAL.navy, PAL.darkSlate, PAL.slate, PAL.gray, PAL.lightGray];

/** Width of the heater shield on each row from y1: straight sides curving to a point. */
const HEATER = [10, 12, 12, 12, 12, 12, 12, 12, 12, 10, 10, 8, 6, 2];

/** Face ramp: wood at tier 0, the tier metal after, kept darker on the bright tiers so sigils show. */
function faceRamp(m: Material, tier: number): readonly Col[] {
  if (tier === 5) return ['#1a0b24', PAL.purple, PAL.magenta, PAL.pink];
  if (tier >= 3) return m.metal.slice(0, 4);
  return m.metal;
}

/**
 * A heater shield with a rim and a boss or emblem. The face changes with the
 * tier: wooden planks with an iron rim and boss, iron, polished steel with a
 * gold rim and cross, faceted crystal, ember with a glowing sun sigil and
 * aether with a glowing star.
 */
export function shield(m: Material, tier: number): Grid {
  const g = new Grid(S, S);
  const F = faceRamp(m, tier);
  const rim = tier === 0 ? IRON.slice(1, 4) : m.trim;
  HEATER.forEach((w, i) => {
    const y = 1 + i;
    for (let x = 8 - w / 2; x < 8 + w / 2; x++) {
      const nx = (x + 0.5 - 8) / 6;
      const ny = (y + 0.5 - 7) / 7;
      let l = 0.78 - nx * 0.38 - ny * 0.12;
      // planks with dark seams on the wooden shield
      if (tier === 0) l += x === 5 || x === 10 ? -0.5 : (x * 5 + y * 3) % 7 === 0 ? -0.15 : 0;
      // crystal facets
      if (tier === 3) l += (Math.floor((x - y * 0.8 + 20) / 3) & 1 ? 0.14 : -0.1) - 0.05;
      g.set(x, y, ramp(F, l, x, y, 0.3));
    }
  });
  // rim round the edge, lit along the top and left
  g.rim((_c, x, _y, s) => (s.n || (s.w && x < 8) ? rim[2] : x < 8 ? rim[1] : rim[0]));
  const glow = m.glow ?? m.gem;
  if (tier <= 1) {
    // a raised ridge down the iron face, studs round the rim and a round iron boss
    if (tier === 1) g.vline(7, 2, 13, m.metal[4]).vline(8, 2, 13, m.metal[1]);
    for (const [x, y] of [
      [5, 1],
      [10, 1],
      [2, 5],
      [13, 5],
      [3, 10],
      [12, 10],
    ] as const)
      g.set(x, y, IRON[4]);
    g.ellipse(8, 6.5, 2.6, 2.6, IRON[1]);
    g.ellipse(8, 6.5, 1.9, 1.9, (x, y, nx, ny) => ramp(IRON, sphereLight(nx, ny, 0.3) + 0.15, x, y, 0.2));
    g.set(7, 5, PAL.white);
  } else if (tier === 2) {
    // a gold cross with a ruby at the crossing
    g.vline(7, 3, 12, m.trim[2]).vline(8, 3, 12, m.trim[1]);
    g.hline(4, 11, 6, m.trim[2]).hline(4, 11, 7, m.trim[1]);
    g.rect(7, 6, 2, 2, m.gem).set(7, 6, m.gemHi);
  } else if (tier === 3) {
    // a cut crystal set in the middle
    g.poly(
      [
        [8, 3],
        [11, 7],
        [8, 11.2],
        [5, 7],
      ],
      m.metal[0],
    );
    g.poly(
      [
        [8, 4],
        [10, 7],
        [8, 10.2],
        [6, 7],
      ],
      (x, y, nx, ny) => ramp([PAL.sky, PAL.cyan, PAL.white], sphereLight(nx, ny, 0.3), x, y, 0.2),
    );
    g.set(7, 6, PAL.white);
  } else {
    // a sigil glowing out of a dark field: a sun for ember, a star for aether
    g.ellipse(8, 7, 3.6, 3.6, mix(F[0], PAL.black, 0.35));
    const hot = tier === 4 ? PAL.yellow : glow;
    const core = PAL.white;
    if (tier === 4) {
      g.ellipse(8, 7, 1.6, 1.6, hot);
      for (const [x, y] of [
        [7, 3],
        [8, 3],
        [7, 10],
        [8, 10],
        [4, 6],
        [4, 7],
        [11, 6],
        [11, 7],
        [5, 4],
        [10, 4],
        [5, 9],
        [10, 9],
      ] as const)
        g.set(x, y, x === 7 || x === 8 ? hot : PAL.gold);
    } else {
      g.vline(7, 3, 10, hot).vline(8, 3, 10, hot).hline(4, 11, 6, hot).hline(4, 11, 7, hot);
      g.set(6, 5, hot).set(9, 5, hot).set(6, 8, hot).set(9, 8, hot);
    }
    g.set(7, 6, core)
      .set(8, 7, core)
      .set(8, 6, mix(hot, core, 0.5))
      .set(7, 7, mix(hot, core, 0.5));
  }
  // a glint on the upper left of the face
  g.set(4, 3, tier === 0 ? PAL.tan : m.metal[4]);
  return finish(g, m);
}

/** Orb colours per tier (dark → light). */
const ORB: readonly (readonly Col[])[] = [
  [PAL.navy, PAL.darkSlate, PAL.slate, PAL.gray, PAL.lightGray],
  [PAL.navy, PAL.blue, PAL.sky, '#7fd3f5', PAL.white],
  ['#2a0f14', PAL.darkRed, PAL.red, PAL.pink, PAL.white],
  [PAL.blue, PAL.sky, PAL.cyan, '#9ff6ff', PAL.white],
  [PAL.darkRed, PAL.red, PAL.orange, PAL.gold, PAL.yellow],
  [PAL.purple, PAL.magenta, PAL.pink, '#9ff6ff', PAL.white],
];

/**
 * A mage's focus: an orb with a swirl of magic inside, held in a claw cradle
 * on a short stem. Tier sets the orb (smoky glass, sapphire, ruby, sky
 * crystal, ember with flame wisps, glowing aether) and the holder (wood, iron,
 * then the tier trim).
 */
export function focus(m: Material, tier: number): Grid {
  const g = new Grid(S, S);
  const H: readonly Col[] = tier === 0 ? m.grip : tier === 1 ? m.metal.slice(1, 4) : m.trim;
  const O = ORB[Math.max(0, Math.min(5, tier))];
  // the orb
  g.ellipse(8, 5.8, 4.3, 4.3, (x, y, nx, ny) => {
    const r = Math.hypot(nx, ny);
    const swirl = Math.sin(Math.atan2(ny, nx) * 2 + r * 6) * (1 - r) * 0.35;
    return ramp(O, sphereLight(nx, ny, 0.3) + swirl, x, y, 0.25);
  });
  g.set(6, 3, PAL.white).set(7, 3, O[4]).set(6, 4, O[3]).set(5, 5, O[3]);
  // the cradle: a cup under the orb with a claw curling up each side
  g.hline(5, 10, 10, H[1]).set(5, 10, H[2]).set(10, 10, H[0]);
  g.hline(6, 9, 11, H[0]).set(6, 11, H[1]);
  g.set(4, 9, H[2]).set(3, 8, H[2]).set(3, 7, H[1]).set(4, 6, H[1]);
  g.set(11, 9, H[1]).set(12, 8, H[1]).set(12, 7, H[0]).set(11, 6, H[0]);
  g.set(8, 9, H[2]).set(7, 9, H[1]);
  // stem and foot
  g.vline(7, 12, 13, H[1]).vline(8, 12, 13, H[0]);
  g.hline(5, 10, 14, H[0]).hline(6, 9, 14, H[1]).set(6, 14, H[2]);
  const out = finish(g, m);
  if (tier === 4) {
    // flame wisps licking up off the orb
    out.set(8, 0, alpha(PAL.yellow, 0.75)).set(7, 1, alpha(PAL.orange, 0.9)).set(9, 1, alpha(PAL.gold, 0.8));
    out.set(11, 1, alpha(PAL.orange, 0.6)).set(4, 2, alpha(PAL.orange, 0.55));
  } else if (tier === 5) {
    // an aether halo
    for (const [x, y] of [
      [2, 4],
      [13, 3],
      [2, 8],
      [14, 7],
      [5, 0],
      [11, 0],
    ] as const)
      out.set(x, y, alpha(PAL.cyan, 0.6));
  } else if (tier === 3) {
    out.set(13, 2, alpha(PAL.white, 0.8)).set(2, 9, alpha(PAL.cyan, 0.7));
  }
  return out;
}
